// @flow

/* eslint-disable-next-line no-unused-vars */
import React, { Fragment, useEffect } from 'react';

import TrialWelcomeModalHeader from '@zapier/design-system-company/src/components/TrialWelcomeModalHeader';
import { ContentLoader } from '@zapier/common-components';
import { useForm } from '@zapier/design-system-company/src/utils';
import {
  Button,
  Dropdown,
  Field,
  Heading,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  responsiveStyles,
} from '@zapier/common-utils';

import options from './options';
import useTrialOnboardingSurvey from './useTrialOnboardingSurvey';

import { type Size } from '@zapier/design-system-company/src/types';

import { type SurveyType } from './types';

/** @jsx isolateComponent */

type Props = {
  /** The current account id (for submitting survey) */
  accountId: number,
  /** Call to change modal route */
  onRoute: string => void,
  /** Size */
  size: Size,
};

const Styles = labelStyles(__filename, {
  body: size =>
    responsiveStyles(size, {
      small: {
        display: 'grid',
        gridGap: 20,
        margin: 20,
        position: 'relative',
      },
      medium: {
        margin: 40,
      },
    }),
  button: {
    textAlign: 'center',
  },
  heading: {
    padding: '44px 0',
    textAlign: 'center',
  },
});

const onValidate = (state: SurveyType) => {
  const validation = {};
  if (!state.companyName) {
    validation.companyName = 'Name is required.';
  }
  return validation;
};

const TrialWelcomeModalSurveyView = (props: Props) => {
  const { error, hasSaved, isLoading, onSaveSurvey } = useTrialOnboardingSurvey(
    {
      accountId: props.accountId,
    }
  );

  const {
    errors,
    handleChange,
    handleChangeEvent,
    handleSubmit,
    isDirty,
    state,
  } = useForm<SurveyType>({
    defaultState: {
      companyName: '',
      companySize: null,
      purchasingRole: null,
      role: null,
      trialReason: null,
    },
    onSubmit: value => {
      onSaveSurvey(value);
    },
    onValidate,
  });

  useEffect(() => {
    if (!error && !isLoading && hasSaved) {
      props.onRoute('payment');
    }
    // eslint-disable-next-line
  }, [error, hasSaved, isLoading]);

  const canSubmit = isDirty;

  return (
    <Fragment>
      <TrialWelcomeModalHeader>
        <div css={Styles.heading}>
          <Heading>
            <Text color="white" type="subtitle900">
              Welcome to Zapier for Companies
            </Text>
          </Heading>
          <Spacer height={8} />
          <Text color="white" type="bodyText900">
            To begin, tell us a bit about your company
          </Text>
        </div>
      </TrialWelcomeModalHeader>

      <form
        css={Styles.body(props.size)}
        onSubmit={
          canSubmit
            ? handleSubmit
            : e => {
                e.preventDefault();
                return false;
              }
        }
      >
        <Field
          error={errors.companyName}
          isRequired={true}
          label="What is your company's name?"
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              ariaLabel="Company Name"
              name="companyName"
              onChange={handleChangeEvent}
              placeholder="Enter company name"
              value={state.companyName}
            />
          )}
        />

        <Field
          label="What is your job title?"
          renderInput={inputProps => (
            <Dropdown
              {...inputProps}
              getLabelForItem={item => item.name}
              items={options.roles}
              menuAriaLabel="Role"
              onChange={r => handleChange(r.id, 'role')}
              placeholder="Select a job title"
            />
          )}
        />

        <Field
          label="What is your role in purchasing Zapier?"
          renderInput={inputProps => (
            <Dropdown
              {...inputProps}
              getLabelForItem={item => item.name}
              items={options.purchasingRoles}
              menuAriaLabel="Purchasing Role"
              onChange={r => handleChange(r.id, 'purchasingRole')}
              placeholder="Select a role"
            />
          )}
        />

        <Field
          label="How many people are in your organization?"
          renderInput={inputProps => (
            <Dropdown
              {...inputProps}
              getLabelForItem={item => item.name}
              items={options.sizes}
              menuAriaLabel="Organization Size"
              onChange={r => handleChange(r.id, 'companySize')}
              placeholder="Select company size"
            />
          )}
        />

        <Field
          label="What is the main reason you started the company trial?"
          renderInput={inputProps => (
            <Dropdown
              {...inputProps}
              getLabelForItem={item => item.name}
              items={options.reasons}
              menuAriaLabel="Reason Started Trial"
              onChange={r => handleChange(r.id, 'trialReason')}
              placeholder="Select an option"
            />
          )}
        />

        <div css={Styles.button}>
          {isLoading ? (
            <ContentLoader
              height="50px"
              width={props.size === 'small' ? '100%' : '103px'}
            >
              <rect
                height="50px"
                rx="10"
                ry="10"
                width={props.size === 'small' ? '100%' : '103px'}
                x="0"
                y="0"
              />
            </ContentLoader>
          ) : (
            <Button
              color="black"
              disabled={!canSubmit}
              isFullWidth={props.size === 'small'}
              type="submit"
            >
              NEXT
            </Button>
          )}
        </div>
        {error && (
          <Text color="red700" type="altText900">
            {error}
          </Text>
        )}
      </form>
    </Fragment>
  );
};

export default TrialWelcomeModalSurveyView;
